import { useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useReducer, useRef, useTransition } from "react";
import clientFetcher from "src/lib/fetcher/client";

const initialState = {
  query: "",
  resultList: [],
  category: "*",
  categoryTitle: "Danh mục",
  /** @type {"idle" | "loading" | "success"} */
  searchStatus: "idle",
};

function searchReducer(state, action) {
  switch (action.type) {
    case "SET_QUERY":
      return { ...state, query: action.payload };
    case "SYNC_QUERY_FROM_URL":
      if (state.query === action.payload) return state;
      return { ...state, query: action.payload };
    case "SET_LOADING":
      return { ...state, searchStatus: "loading" };
    case "SET_RESULTS":
      return {
        ...state,
        resultList: action.payload,
        searchStatus: "success",
      };
    case "RESET_RESULTS":
      return { ...state, resultList: [], searchStatus: "idle" };
    case "SET_CATEGORY":
      return {
        ...state,
        category: action.payload.id,
        categoryTitle: action.payload.name,
      };
    default:
      return state;
  }
}

function debounceMsForQuery(trimmed) {
  if (trimmed.length <= 2) return 180;
  return 420;
}

const SEARCH_INPUT_PROPS = {
  type: "search",
  enterKeyHint: "search",
  autoComplete: "off",
  autoCorrect: "off",
  autoCapitalize: "off",
  spellCheck: false,
};

const useSearch = () => {
  const router = useRouter();
  const [state, dispatch] = useReducer(searchReducer, initialState);
  const [_, startTransition] = useTransition();
  const searchParams = useSearchParams();
  const qFromUrl = searchParams.get("q") ?? "";

  const debounceTimerRef = useRef(null);
  const searchRequestIdRef = useRef(0);
  const containerRef = useRef(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  const cancelPendingSearch = useCallback(() => {
    if (debounceTimerRef.current != null) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }
  }, []);

  const runSearch = useCallback((rawValue, categoryId) => {
    const trimmed = (rawValue ?? "").trim();
    if (!trimmed) {
      searchRequestIdRef.current += 1;
      dispatch({ type: "RESET_RESULTS" });
      return;
    }
    searchRequestIdRef.current += 1;
    const requestId = searchRequestIdRef.current;
    dispatch({ type: "SET_LOADING" });
    const categoryArg = categoryId && categoryId !== "*" ? categoryId : undefined;
    clientFetcher.searchProducts(trimmed, categoryArg).then((data) => {
      if (requestId !== searchRequestIdRef.current) return;
      dispatch({ type: "SET_RESULTS", payload: data ?? [] });
    });
  }, []);

  useEffect(() => {
    dispatch({ type: "SYNC_QUERY_FROM_URL", payload: qFromUrl });
  }, [qFromUrl]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        dispatch({ type: "RESET_RESULTS" });
      }
    };
    document.addEventListener("click", onDocClick);
    return () => {
      document.removeEventListener("click", onDocClick);
      cancelPendingSearch();
      searchRequestIdRef.current += 1;
    };
  }, [cancelPendingSearch]);

  const handleSearch = useCallback(
    (e) => {
      const value = e.target?.value ?? "";
      dispatch({ type: "SET_QUERY", payload: value });
      cancelPendingSearch();
      const trimmed = value.trim();
      if (!trimmed) {
        searchRequestIdRef.current += 1;
        dispatch({ type: "RESET_RESULTS" });
        return;
      }
      const delay = debounceMsForQuery(trimmed);
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        const cat = stateRef.current.category;
        startTransition(() => runSearch(value, cat));
      }, delay);
    },
    [cancelPendingSearch, runSearch, startTransition],
  );

  const handleCategoryChange = useCallback(
    (cat) => () => {
      dispatch({
        type: "SET_CATEGORY",
        payload: { id: cat.id, name: cat.name },
      });
      cancelPendingSearch();
      searchRequestIdRef.current += 1;
      const q = stateRef.current.query;
      debounceTimerRef.current = setTimeout(() => {
        debounceTimerRef.current = null;
        startTransition(() => runSearch(q, cat.id));
      }, 0);
    },
    [cancelPendingSearch, runSearch, startTransition],
  );

  const handleSearchByUrlParam = useCallback(() => {
    const raw = stateRef.current.query.trim();
    const params = new URLSearchParams(searchParams.toString());
    if (!raw) {
      params.delete("q");
    } else {
      params.set("q", raw);
    }
    startTransition(() => {
      if (raw) {
        params.delete("page");
        const qs = params.toString();
        router.push(`/collection${qs ? `?${qs}` : ""}`);
      }
    });
  }, [router, searchParams, startTransition]);

  return {
    query: state.query,
    category: state.category,
    containerRef,
    resultList: state.resultList,
    isSearchLoading: state.searchStatus === "loading",
    handleSearch,
    handleSearchByUrlParam,
    categoryTitle: state.categoryTitle,
    handleCategoryChange,
    searchInputProps: SEARCH_INPUT_PROPS,
  };
};

export default useSearch;
